'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  BrandImageData,
  BrandImageProps,
  BrandImageTrackingParams,
  BrandImageValidExposureParams,
} from './types';

const DEFAULT_BACKGROUND_COLOR = '#463AE8';
const DEFAULT_TEXT_COLOR = '#FFFFFF';

function getPlainText(value: string) {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getConfiguredTitle(indexData: BrandImageData) {
  return [indexData.title, indexData.des_title, indexData.sub_title].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
}

function useTrackingParams(
  indexData: BrandImageData,
  moduleIndex: number,
): BrandImageTrackingParams {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: indexData.component_id,
      module_id: String(indexData.blocks_advances_id || ''),
      module_title: getConfiguredTitle(indexData) || '',
      mkt_index: 0,
      mkt_index_sub: '',
      mkt_jump_type: String(indexData.jump_type || ''),
      mkt_jump_content: String(indexData.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(indexData.sku?.shop_sku || ''),
      business_sku: String(indexData.sku?.business_sku || ''),
      product_sku: String(indexData.sku?.sku || ''),
      product_id: String(indexData.sku?.id || ''),
      product_name: String(indexData.sku?.short_name || ''),
      group_id: String(indexData.product_group?.id || ''),
      group_name: String(indexData.product_group?.name || ''),
    }),
    [indexData, moduleIndex],
  );
}

function buildValidExposureParams(
  indexData: BrandImageData,
  moduleIndex: number,
): BrandImageValidExposureParams {
  return {
    module_index: moduleIndex,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: getConfiguredTitle(indexData) || '',
    module_name: indexData.component_id,
    module_tab_name: '',
    mkt_index: 0,
    mkt_index_sub: '',
    mkt_jump_type: String(indexData.jump_type ?? ''),
    mkt_jump_content: String(indexData.Jump_target ?? ''),
  };
}

function useTrackingHandlers({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<BrandImageProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: BrandImageTrackingParams;
  validExposureParams: BrandImageValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC brand image exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC brand image click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    handleClick,
    sectionRef: useMergedRefs(exposureRef, validExposureRef),
  };
}

export default function BrandImage({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BrandImageProps) {
  const title = getConfiguredTitle(indexData);
  const hasContent = Boolean(indexData.jump_image || title || indexData.des);
  const trackingParams = useTrackingParams(indexData, moduleIndex);
  const validExposureParams = buildValidExposureParams(indexData, moduleIndex);
  const { handleClick, sectionRef } = useTrackingHandlers({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const jumpTarget = useMemo<JumpTarget>(
    () => ({
      jump_type: indexData.jump_type,
      Jump_target: indexData.Jump_target,
      ...(indexData.sku?.shop_sku ? { sku: { shop_sku: indexData.sku.shop_sku } } : undefined),
      ...(indexData.product_group?.id || indexData.product_group?.path
        ? { product_group: indexData.product_group }
        : undefined),
    }),
    [indexData.Jump_target, indexData.jump_type, indexData.product_group, indexData.sku],
  );

  if (!hasContent) return null;

  const textColor = indexData.text_color?.trim() || DEFAULT_TEXT_COLOR;
  const titleText = title ? getPlainText(title) : '';

  return (
    <div
      ref={sectionRef}
      className="w-full"
      style={{ backgroundColor: indexData.bg_color?.trim() || DEFAULT_BACKGROUND_COLOR }}
    >
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={title || indexData.des || 'Brand image'}
        className="block w-full"
      >
        <section className="flex h-[460px] w-full items-center px-[48px] xl:px-[72px]">
          <div className="flex w-full items-start gap-[80px]">
            <div className="flex h-[47px] w-[350px] shrink-0 items-center xl:h-[67px] xl:w-[500px]">
              {indexData.jump_image ? (
                <Image
                  src={indexData.jump_image}
                  alt="ZEELOOL Official"
                  width={indexData.jump_img_width || 660}
                  height={indexData.jump_img_height || 88}
                  loading={moduleIndex < 3 ? 'eager' : 'lazy'}
                  priority={moduleIndex < 3}
                  sizes="(min-width: 1280px) 500px, 350px"
                  className="block size-full object-contain object-left"
                />
              ) : null}
            </div>

            <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-[16px]">
              {titleText ? (
                <b
                  className="block w-full font-br text-[40px] leading-[48px]"
                  style={{ color: textColor }}
                >
                  {titleText}
                </b>
              ) : null}

              {indexData.des ? (
                <h3
                  className="w-full whitespace-pre-line font-ir text-[16px] leading-[24px] opacity-80"
                  style={{ color: textColor }}
                  dangerouslySetInnerHTML={{ __html: indexData.des }}
                />
              ) : null}
            </div>
          </div>
        </section>
      </MagicJump>
    </div>
  );
}
